'use client';

// Thin app-side wrapper around the framework-pure @gam/editor TiptapEditor.
// It injects the app-only dependencies (i18n translations + keyboard-layout
// converter) so every consumer keeps working unchanged.
import { TiptapEditor as PkgTiptapEditor, objectRefUrl } from '@gam/editor';
import type { TiptapEditorProps } from '@gam/editor';
import { useRouter } from 'next/navigation';
import { useSettings } from '@/contexts/SettingsContext';
import { getTranslations } from '@/lib/i18n';
import { toHebrew, toEnglish } from '@/lib/gibberish';
import { createNote, createNoteRelation, deleteNote, searchNotes } from '@/lib/supabase/entityQueries';
import { TASK_ENTITY_TYPE } from '@/lib/supabase/taskLibraryQueries';
import { TASK_OF_RELATION } from '@/lib/supabase/instanceTaskLinksQueries';
import { createClient } from '@/lib/supabase/client';
import { uploadFile } from '@/lib/supabase/storageQueries';
import { documentMediaNodeView } from './DocumentMediaBlock';
import type { AiTextActionKind, TextToRecordKind } from '@gam/editor';

/**
 * What each AI menu row asks the model to do. The prompt insists on bare
 * replacement text: whatever comes back is inserted straight over the user's
 * selection, so a preamble like "Here is the shortened version" would land in
 * the document.
 */
const AI_INSTRUCTIONS: Record<AiTextActionKind, string> = {
  shorten: 'קצר את הטקסט הבא ושמור על המשמעות ועל שפת המקור.',
  rewrite: 'שכתב את הטקסט הבא בניסוח אחר, באותה שפה ובאותו אורך בקירוב.',
  translate: 'תרגם את הטקסט הבא — עברית לאנגלית, וכל שפה אחרת לעברית.',
  improve: 'שפר את הניסוח, הדקדוק והפיסוק של הטקסט הבא, בלי לשנות את המשמעות ובלי להוסיף תוכן.',
};

/**
 * Runs one AI action against the existing /api/ai/chat route (mode "write")
 * and returns the finished text. The route answers as an SSE stream, so the
 * chunks are collected here and handed back as a single string — the toolbar
 * replaces the selection once, not on every token.
 */
async function runAiTextAction(kind: AiTextActionKind, text: string): Promise<string> {
  // The route authenticates on the Authorization header alone — a cookie
  // session is not enough — so the access token is attached explicitly.
  const { data: { session } } = await createClient().auth.getSession();
  if (!session?.access_token) throw new Error('צריך להתחבר כדי להשתמש בעוזר');

  const res = await fetch('/api/ai/chat', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      mode: 'write',
      messages: [
        {
          role: 'user',
          content: `${AI_INSTRUCTIONS[kind]}\n\nהחזר אך ורק את הטקסט המתוקן, בלי הקדמה, בלי הסבר ובלי מרכאות.\n\n---\n${text}`,
        },
      ],
    }),
  });

  if (!res.ok || !res.body) {
    // The message reaches the toolbar and is shown to the user as-is, so it
    // says what to do rather than what broke.
    if (res.status === 401) throw new Error('צריך להתחבר מחדש כדי להשתמש בעוזר');
    if (res.status === 429) throw new Error('חרגת ממכסת השימוש, נסה שוב בעוד כמה דקות');
    throw new Error(`העוזר לא הגיב (${res.status})`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let out = '';

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE frames are separated by a blank line; a partial frame stays in the
    // buffer until its terminator arrives.
    const frames = buffer.split('\n\n');
    buffer = frames.pop() ?? '';
    for (const frame of frames) {
      const line = frame.trim();
      if (!line.startsWith('data:')) continue;
      // Parsing and handling are deliberately separate. When the throw for a
      // server-reported error sat inside the try, the catch below — which
      // exists only to skip frames that are not ours — swallowed it, and the
      // button looked dead while the server had in fact said what went wrong.
      let evt: { type?: string; text?: string; error?: string } | null = null;
      try {
        evt = JSON.parse(line.slice(5).trim());
      } catch {
        // A frame that is not JSON is not ours — skip it rather than aborting
        // a rewrite that is otherwise arriving fine.
        continue;
      }
      if (!evt) continue;
      if (evt.type === 'error') throw new Error(evt.error || 'העוזר החזיר שגיאה');
      if (evt.type === 'text' && typeof evt.text === 'string') out += evt.text;
    }
  }

  const result = out.trim();
  // An empty stream is a failure too, and must not pass as "nothing changed".
  if (!result) throw new Error('העוזר החזיר תשובה ריקה');
  return result;
}

/**
 * Turns selected/block text into a real record — today only `task`, via the
 * exact same createNote(text, TASK_ENTITY_TYPE, {status:'todo'}) convention
 * used by taskSubtasksQueries/instanceTaskLinksQueries (Rule 10 — no second
 * task-creation path). The caller (FloatingToolbar) inserts an ObjectRef
 * chip linking to the returned {id, title} — Gal approved that inline link
 * explicitly (22.09 questionnaire round 2), overriding the round-1 Q2 "no
 * auto-link" answer.
 *
 * Also writes the host link — `note_relations` row, relation_type='task_of',
 * source=new task, target=hostId — the same pattern as
 * instanceTaskLinksQueries.addInstanceTask, so the new task shows up in the
 * host record's Tasks tab. Gal reversed the earlier "no linking" decision
 * (22.09 DEC-0452 comment) on 23.09: editor-created vTasks must link. If the
 * relation write fails, the note is rolled back (deleted) so no orphan task
 * survives a failed link — same safety as addInstanceTask.
 */
async function createFromText(
  kind: TextToRecordKind,
  text: string,
  hostId?: string,
): Promise<{ id: string; title: string } | null> {
  if (kind !== 'task') return null; // only wired kind today — see TextToRecordKind
  const note = await createNote(text, TASK_ENTITY_TYPE, { status: 'todo' });
  if (!note) return null;
  if (hostId) {
    const link = await createNoteRelation(note.id, hostId, TASK_OF_RELATION);
    if (!link) {
      // Same safety as addInstanceTask: a task that failed to link to its
      // host must not survive as a silent orphan.
      await deleteNote(note.id);
      console.error('createFromText: task_of link failed, task rolled back');
      return null;
    }
  }
  return { id: note.id, title: note.title };
}

export function TiptapEditor(props: TiptapEditorProps) {
  const { language } = useSettings();
  const router = useRouter();
  const translations = getTranslations(language).editor;
  return (
    <PkgTiptapEditor
      {...props}
      mediaNodeView={documentMediaNodeView}
      uploadAttachment={async file => {
        const { data: { user } } = await createClient().auth.getUser();
        if (!user) throw new Error('צריך להתחבר כדי להעלות קובץ');
        const result = await uploadFile(user.id, file);
        if (!result) throw new Error('העלאת הקובץ נכשלה — אפשר לנסות שוב');
        return { path: result.path, url: result.url, filename: file.name, mime: file.type, bytes: file.size };
      }}
      translations={translations}
      gibberishConverter={{ toHebrew, toEnglish }}
      aiTextAction={runAiTextAction}
      searchEntities={(q) =>
        searchNotes(q).then((rs) =>
          rs.map((r) => ({ id: r.id, title: r.title, entityType: r.entity_type }))
        )
      }
      onNavigateRef={(ref) => router.push(objectRefUrl({ id: ref.id, entityType: ref.entityType }))}
      onCreateFromText={(kind, text) => createFromText(kind, text, props.recordId)}
    />
  );
}

export default TiptapEditor;
